import { EmissionRef } from './emission.model';
import { Transportation } from './transportation.model';
import { CarbonCredit } from './carbon-credit.model';
import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';

export interface GhgEmissionsInterface {
  landfillPercent: UnitValue;
  incinerationPercent: UnitValue;
  compostingPercent: UnitValue;
  includeWasteDisposal: boolean;
  isTransportation: boolean;
  transportations: Transportation[];
  carbonCredit: CarbonCredit;
  isEmissionCustom: boolean;
  electricEmission: EmissionRef;
  natgasEmission: EmissionRef;
  fertilizerEmission: EmissionRef;
  biomethEmission: EmissionRef;
  landfillEmission: EmissionRef;
  ssoEmission: EmissionRef;
  mwEmission: EmissionRef;
  agriEmission: EmissionRef;
  forestEmission: EmissionRef;
  municipalEmission: EmissionRef;
}

export class GhgEmissions {
  public landfillPercent: UnitValue;
  public incinerationPercent: UnitValue;
  public compostingPercent: UnitValue;
  public includeWasteDisposal: boolean;
  public isTransportation: boolean;
  public transportations: Transportation[];
  public carbonCredit: CarbonCredit;
  public isEmissionCustom: boolean;
  public electricEmission: EmissionRef;
  public natgasEmission: EmissionRef;
  public fertilizerEmission: EmissionRef;
  public biomethEmission: EmissionRef;
  public landfillEmission: EmissionRef;
  public ssoEmission: EmissionRef;
  public mwEmission: EmissionRef;
  public agriEmission: EmissionRef;
  public forestEmission: EmissionRef;
  public municipalEmission: EmissionRef;

  constructor(param: GhgEmissionsInterface = {
    landfillPercent: new UnitValue(),
    incinerationPercent: new UnitValue(),
    compostingPercent: new UnitValue(),
    includeWasteDisposal: false,
    isTransportation: false,
    transportations: [],
    carbonCredit: new CarbonCredit(),
    isEmissionCustom: false,
    electricEmission: new EmissionRef(),
    natgasEmission: new EmissionRef(),
    fertilizerEmission: new EmissionRef(),
    biomethEmission: new EmissionRef(),
    landfillEmission: new EmissionRef(),
    ssoEmission: new EmissionRef(),
    mwEmission: new EmissionRef(),
    agriEmission: new EmissionRef(),
    forestEmission: new EmissionRef(),
    municipalEmission: new EmissionRef()
  }) {
    this.landfillPercent = new UnitValue(param.landfillPercent);
    this.incinerationPercent = new UnitValue(param.incinerationPercent);
    this.compostingPercent = new UnitValue(param.compostingPercent);
    this.includeWasteDisposal = param.includeWasteDisposal;
    this.isTransportation = param.isTransportation;
    this.transportations = [];
    for (const transport of param.transportations) {
      this.transportations.push(new Transportation(transport));
    }
    this.carbonCredit = new CarbonCredit(param.carbonCredit);
    this.isEmissionCustom = param.isEmissionCustom;
    this.electricEmission = new EmissionRef(param.electricEmission);
    this.natgasEmission = new EmissionRef(param.natgasEmission);
    this.fertilizerEmission = new EmissionRef(param.fertilizerEmission);
    this.biomethEmission = new EmissionRef(param.biomethEmission);
    this.landfillEmission = new EmissionRef(param.landfillEmission);
    this.ssoEmission = new EmissionRef(param.ssoEmission);
    this.mwEmission = new EmissionRef(param.mwEmission);
    this.agriEmission = new EmissionRef(param.agriEmission);
    this.forestEmission = new EmissionRef(param.forestEmission);
    this.municipalEmission = new EmissionRef(param.municipalEmission);
  }

  static deepCopy(ghgEmissions: GhgEmissions) {
    let copy: GhgEmissions;
    if (ghgEmissions) {
      copy = new GhgEmissions(ghgEmissions);
      return copy;
    }
    throw new Error('greenhouse gas emissions copy failed');
  }

  getInputSummary(path: string[]) {
    let inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    if (this.isEmissionCustom) {
      inputSummary = this.getCustomEmissionInputs(inputSummary, path);
    } else {
      inputSummary.subSection[GhgEmissionsPropName.electricEmission] = this.electricEmission.getInputSummary([...path, 'electricEmission']);
    }
    inputSummary.subSection[GhgEmissionsPropName.transportations] = this.getTransportationInputSummary(path);
    inputSummary.subSection[GhgEmissionsPropName.carbonCredit] = this.carbonCredit.getInputSummary([...path, 'carbonCredit']);
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.includeWasteDisposal, this.includeWasteDisposal, [...path, 'includeWasteDisposal']);
    if (this.includeWasteDisposal) {
      inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.landfillPercent, this.landfillPercent, [...path, 'landfillPercent']);
      inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.incinerationPercent, this.incinerationPercent, [...path, 'incinerationPercent']);
      inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.compostingPercent, this.compostingPercent, [...path, 'compostingPercent']);
    }
    inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.isTransportation, this.isTransportation, [...path, 'isTransportation']);
    inputs = InputSummaryUtils.performPush(inputs, GhgEmissionsPropName.isEmissionCustom, this.isEmissionCustom, [...path, 'isEmissionCustom']);
    return inputs;
  }

  getTransportationInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    for (const transport of this.transportations) {
      const index = this.transportations.indexOf(transport);
      inputSummary.subSection[transport.nameTranslation] = transport.getInputSummary([...path, 'transportations', String(index)]);
    }
    return inputSummary;
  }

  getCustomEmissionInputs(inputSummary: InputSummary, path: string[]) {
    inputSummary.subSection[GhgEmissionsPropName.electricEmission] = this.electricEmission.getInputSummary([...path, 'electricEmission']);
    inputSummary.subSection[GhgEmissionsPropName.natgasEmission] = this.natgasEmission.getInputSummary([...path, 'natgasEmission']);
    inputSummary.subSection[GhgEmissionsPropName.fertilizerEmission] = this.fertilizerEmission.getInputSummary([...path, 'fertilizerEmission']);
    inputSummary.subSection[GhgEmissionsPropName.biomethEmission] = this.biomethEmission.getInputSummary([...path, 'biomethEmission']);
    inputSummary.subSection[GhgEmissionsPropName.ssoEmission] = this.ssoEmission.getInputSummary([...path, 'ssoEmission']);
    inputSummary.subSection[GhgEmissionsPropName.mwEmission] = this.mwEmission.getInputSummary([...path, 'mwEmission']);
    inputSummary.subSection[GhgEmissionsPropName.agriEmission] = this.agriEmission.getInputSummary([...path, 'agriEmission']);
    inputSummary.subSection[GhgEmissionsPropName.forestEmission] = this.forestEmission.getInputSummary([...path, 'forestEmission']);
    inputSummary.subSection[GhgEmissionsPropName.municipalEmission] = this.municipalEmission.getInputSummary([...path, 'municipalEmission']);
    inputSummary.subSection[GhgEmissionsPropName.landfillEmission] = this.landfillEmission.getInputSummary([...path, 'landfillEmission']);
    return inputSummary;
  }
}

export enum GhgEmissionsPropName {
  landfillPercent = 'INPUT_SUMMARY.GHG_EMISSIONS.LANDFILL_PERCENT',
  incinerationPercent = 'INPUT_SUMMARY.GHG_EMISSIONS.INCINERATION_PERCENT',
  compostingPercent = 'INPUT_SUMMARY.GHG_EMISSIONS.COMPOSTING_PERCENT',
  includeWasteDisposal = 'INPUT_SUMMARY.GHG_EMISSIONS.INCLUDE_WASTE_DISPOSAL',
  transportations = 'INPUT_SUMMARY.GHG_EMISSIONS.TRANSPORTATION',
  carbonCredit = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT',
  isTransportation = 'INPUT_SUMMARY.GHG_EMISSIONS.IS_TRANSPORTATION',
  isEmissionCustom = 'INPUT_SUMMARY.GHG_EMISSIONS.IS_EMISSION_CUSTOM',
  electricEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.ELECTRIC_EMISSION',
  natgasEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.NATGAS_EMISSION',
  fertilizerEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.FERTILIZER_EMISSION',
  biomethEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.BIOMETH_EMISSION',
  landfillEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.LANDFILL_EMISSION',
  ssoEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.SSO_EMISSION',
  mwEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.MW_EMISSION',
  agriEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.AGRI_EMISSION',
  forestEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.FOREST_EMISSION',
  municipalEmission = 'INPUT_SUMMARY.GHG_EMISSIONS.MUNICIPAL_EMISSION',
  id_landfillPercent = 'landfill percent',
  id_incinerationPercent = 'incineration percent',
  id_compostingPercent = 'composting percent',
  id_transportations = 'transportation',
  id_carbonCredit = 'carbon credit',
  id_isTransportation = 'transportation change',
  id_isEmissionCustom = 'custom emission',
  id_electricEmission = 'electrical',
  id_natgasEmission = 'energy production: natural gas ',
  id_fertilizerEmission = 'energy production: biofertilizer',
  id_biomethEmission = 'energy production: biomethane',
  id_landfillEmission = 'landfill (burn)',
  id_ssoEmission = 'composting: municipal material',
  id_mwEmission = 'composting: all other materials',
  id_agriEmission = 'incineration: agricultural / energy crops / biological materials',
  id_forestEmission = 'incineration: forest materials',
  id_municipalEmission = 'incineration: municipal materials',
}

